import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { PageBanner } from "./Attorneys";
import { FIRM } from "../mock";

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    // Store to localStorage so it feels functional (mock only)
    const existing = JSON.parse(localStorage.getItem("nw_messages") || "[]");
    existing.push({ ...form, ts: new Date().toISOString() });
    localStorage.setItem("nw_messages", JSON.stringify(existing));
    setSubmitted(true);
    setForm({
      name: "",
      email: "",
      phone: "",
      subject: "General Inquiry",
      message: "",
    });
    setTimeout(() => setSubmitted(false), 4500);
  };

  return (
    <div className="bg-white">
      <PageBanner
        eyebrow="Contact"
        title="Get in Touch"
        subtitle="We are available to discuss new matters, referrals, and mediation availability. Reach out and a founding partner will follow up personally."
      />

      <section className="py-20 md:py-24">
        <div className="container-nw grid lg:grid-cols-12 gap-12">
          {/* Left: form */}
          <div className="lg:col-span-7">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-3">
              Send a Message
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy">
              How can we help?
            </h2>
            <div className="h-0.5 w-14 bg-nw-gold mt-4 mb-8" />

            {submitted && (
              <div className="mb-6 flex items-start gap-3 bg-nw-cream border-l-4 border-nw-gold p-4 text-nw-navy">
                <CheckCircle2 className="w-5 h-5 text-nw-gold flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <strong className="font-serif-display text-lg block">
                    Message received.
                  </strong>
                  Thank you for reaching out. A member of our team will contact you shortly.
                </div>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-nw-navy/70 mb-2 block">
                    Full Name
                  </label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="w-full border border-nw-stone focus:border-nw-gold outline-none px-4 py-3 text-nw-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-nw-navy/70 mb-2 block">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    className="w-full border border-nw-stone focus:border-nw-gold outline-none px-4 py-3 text-nw-navy transition-colors"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-nw-navy/70 mb-2 block">
                    Phone
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="w-full border border-nw-stone focus:border-nw-gold outline-none px-4 py-3 text-nw-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-nw-navy/70 mb-2 block">
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    className="w-full border border-nw-stone focus:border-nw-gold outline-none px-4 py-3 text-nw-navy transition-colors bg-white"
                  >
                    <option>General Inquiry</option>
                    <option>Construction Litigation</option>
                    <option>Insurance Coverage</option>
                    <option>Mediation</option>
                    <option>Personal Injury</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-nw-navy/70 mb-2 block">
                  Brief Description of Your Matter
                </label>
                <textarea
                  required
                  rows={6}
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  className="w-full border border-nw-stone focus:border-nw-gold outline-none px-4 py-3 text-nw-navy transition-colors resize-none"
                />
              </div>
              <p className="text-xs text-nw-navy/60 leading-relaxed">
                Submitting this form does not create an attorney-client
                relationship. Please do not send confidential information
                until such a relationship has been established.
              </p>
              <button
                type="submit"
                className="group inline-flex items-center gap-2 bg-nw-navy hover:bg-nw-navy-light text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-all"
              >
                Send Message
                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </div>

          {/* Right: info */}
          <aside className="lg:col-span-5">
            <div className="bg-nw-navy text-white p-8 lg:p-10">
              <div className="text-nw-gold-light text-xs uppercase tracking-[0.3em] mb-3">
                Charleston Office
              </div>
              <h3 className="font-serif-display text-3xl">
                Visit or call the firm.
              </h3>
              <div className="h-0.5 w-12 bg-nw-gold mt-4 mb-8" />

              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-nw-gold mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-white/85">
                    <div className="font-semibold text-white mb-1">Address</div>
                    <div>{FIRM.address.street}</div>
                    <div>
                      {FIRM.address.city}, {FIRM.address.state} {FIRM.address.zip}
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-nw-gold mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-white/85">
                    <div className="font-semibold text-white mb-1">Telephone</div>
                    <a href={`tel:${FIRM.phone}`} className="hover:text-nw-gold-light">
                      {FIRM.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-nw-gold mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-white/85">
                    <div className="font-semibold text-white mb-1">Email</div>
                    <a href={`mailto:${FIRM.email}`} className="hover:text-nw-gold-light">
                      {FIRM.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-nw-gold mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-white/85">
                    <div className="font-semibold text-white mb-1">Hours</div>
                    <div>Monday – Friday</div>
                    <div>8:30 AM – 5:30 PM ET</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-6 aspect-[4/3] overflow-hidden border border-nw-stone">
              <iframe
                title="Charleston Office Map"
                className="w-full h-full"
                src="https://www.google.com/maps?q=Charleston+SC+Church+Street&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
